import { yupResolver } from '@hookform/resolvers/yup'
import { Close, AttachFile } from '@mui/icons-material'
import {
  CardActions,
  CardContent,
  Divider,
  Grid,
  Typography
} from '@mui/material'
import {
  Button,
  Card,
  CardHeader,
  IconButton,
  Modal,
  TextFieldC
} from 'components'
import { FormProvider, useForm } from 'react-hook-form'
import { Yup } from 'utils'
import { Attachments } from 'modules/orders/components/Tracking/Attachments'
import { FileUploadProvider, useFiles } from 'contexts'
import fileSize from 'filesize'
import { ATTACHMENT_SIZE_LIMIT } from 'config'
import { useAttachFilesStore } from './attachFilesStore'
import { useAttachFiles } from 'modules/orders/hooks/useAttachFiles'

type FormData = {
  description?: string
}

const schema = Yup.object({
  description: Yup.string()
})

function AttachFilesModalProvided() {
  const { uploadedFiles: trackingFiles } = useFiles()
  const isOpen = useAttachFilesStore.useIsModalOpen()
  const setIsOpenModal = useAttachFilesStore.useSetModalOpen()
  const orderId = useAttachFilesStore.useOrderId()
  const proposalId = useAttachFilesStore.useProposalId()

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      description: ''
    }
  })

  const { handleSubmit, reset } = methods

  const { mutateAsync, isLoading } = useAttachFiles()

  const onClose = () => {
    setIsOpenModal(false)
    reset()
  }

  const onSubmit = async (data: FormData) => {
    try {
      const normalizedData = {
        files: trackingFiles.map((file) => file.path),
        description: data.description || undefined,
        orderId: orderId,
        proposalId: proposalId
      }
      await mutateAsync(normalizedData)
      onClose()
    } catch {}
  }

  const uploading = trackingFiles.some(
    (item) => (item?.progress || 0) < 100 && !item.uploaded
  )

  const isNotUploadedItem =
    trackingFiles.some((item) => !item.uploaded) || !trackingFiles.length
  const totalSize = trackingFiles.reduce(
    (acc, cur) => acc + (cur?.file?.size || 0),
    0
  )

  const isExceededSizeLimit = totalSize >= ATTACHMENT_SIZE_LIMIT

  const entityId = orderId || proposalId
  const entityType = orderId ? 'Pedido' : 'Orçamento'

  return (
    <FormProvider {...methods}>
      <Modal
        open={isOpen}
        onClose={onClose}
        keepMounted={false}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      >
        <Card style={{ padding: 0, minWidth: 300, width: 600 }}>
          <CardHeader
            title={`Anexar Arquivos - ${entityType}: ${entityId || ''}`}
            HeaderRight={
              <IconButton onClick={() => onClose()}>
                <Close />
              </IconButton>
            }
          />
          <Divider />

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextFieldC
                  name="description"
                  label="Descrição (opcional)"
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12}>
                <Attachments multiple={true} limitFiles={15} />
              </Grid>
              {isExceededSizeLimit && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error">
                    A soma dos tamanhos de cada arquivo deve ser menor que{' '}
                    {fileSize(ATTACHMENT_SIZE_LIMIT)}!
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
          <Divider />
          <CardActions>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '1rem'
              }}
            >
              <Button
                variant="text"
                sx={{
                  width: { sm: 100, md: 200 }
                }}
                onClick={() => onClose()}
              >
                Cancelar
              </Button>
              <Button
                sx={{
                  width: { sm: 100, md: 200 }
                }}
                type="button"
                disabled={
                  uploading ||
                  isNotUploadedItem ||
                  isLoading ||
                  isExceededSizeLimit
                }
                onClick={handleSubmit(onSubmit)}
                startIcon={<AttachFile />}
              >
                Anexar Arquivos
              </Button>
            </div>
          </CardActions>
        </Card>
      </Modal>
    </FormProvider>
  )
}

export const AttachFilesModal = () => (
  <FileUploadProvider>
    <AttachFilesModalProvided />
  </FileUploadProvider>
)

export default AttachFilesModal
